import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Provide helpful context for Clerk errors
    if (error.message?.includes('Clerk') || error.message?.includes('failed_to_load_clerk_js')) {
      console.error('Clerk script loading failed. This is usually caused by:');
      console.error('1. Network connectivity issues - Check your internet connection');
      console.error('2. Firewall or network restrictions - Check if Clerk domains are blocked');
      console.error('3. CORS configuration issues - Check browser console for CORS errors');
      console.error('4. Invalid or incorrect Clerk publishable key - Verify REACT_APP_CLERK_PUBLISHABLE_KEY in .env.local');
      console.error('5. Clerk service outage - Check Clerk status page');
      console.error('Please check your network connection and Clerk configuration.');
    }
    
    this.setState({
      errorInfo,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const isClerkError = this.state.error?.message?.includes('Clerk') || 
                          this.state.error?.message?.includes('failed_to_load_clerk_js');
      
      return (
        <div style={{ padding: '20px', textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
          <h2>Something went wrong.</h2>
          <p style={{ marginBottom: '20px' }}>{this.state.error?.message}</p>
          
          {isClerkError && (
            <div style={{ 
              background: '#fff3cd', 
              border: '1px solid #ffc107', 
              borderRadius: '4px', 
              padding: '15px', 
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              <h3 style={{ marginTop: 0 }}>Clerk Authentication Error</h3>
              <p>This error usually indicates a network or configuration issue:</p>
              <ul style={{ textAlign: 'left', marginBottom: 0 }}>
                <li>Check your internet connection</li>
                <li>Verify your Clerk publishable key in <code>.env.local</code></li>
                <li>Check if firewall/network is blocking Clerk domains</li>
                <li>Try refreshing the page or clearing browser cache</li>
              </ul>
            </div>
          )}
          
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              cursor: 'pointer',
              backgroundColor: '#38BBD1',
              color: 'white',
              border: 'none',
              borderRadius: '4px'
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

